import React, { useState, useEffect } from 'react';
import logo from '../../assets/logo-lockup.png';
import LegalFooterLinks from '../Legal/LegalFooterLinks';
import AuthHomeLink from './AuthHomeLink';
import './AuthPage.css';

const FALLBACK_DISCLAIMER_TEXT =
  'cutthe6.com is a technology platform that provides tools and information to assist with real estate workflows. '
  + 'This content is educational and organizational only, and is not legal, financial, or real estate advice.';

const FALLBACK_CHECKBOX_LABEL =
  'I acknowledge that cutthe6.com does not provide legal, financial, or real estate advice, and I agree to the Terms of Service and Privacy Notice.';

/**
 * RegisterPage: Full registration form.
 * Calls onRegister(data) on submit.
 * Calls onSwitchToLogin() when user clicks "Sign in".
 * signupIntent="list_home" (from the "start your listing" page) asks the
 * backend for seller access and says so on the form.
 */
const RegisterPage = ({ onRegister, onSwitchToLogin, onHome, error, signupIntent = null }) => {
  const listingSignup = signupIntent === 'list_home';
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
    role: 'buyer',
    phone: '',
  });
  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);
  const [disclaimer, setDisclaimer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetch('/api/legal/signup-disclaimer')
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to load disclaimer (HTTP ${res.status})`);
        }
        return res.json();
      })
      .then((data) => setDisclaimer({
        version: data?.version || 'current',
        effective_date: data?.effective_date || 'now',
        text: data?.text || FALLBACK_DISCLAIMER_TEXT,
        checkbox_label: data?.checkbox_label || FALLBACK_CHECKBOX_LABEL,
      }))
      .catch((err) => {
        console.error('Failed to load disclaimer:', err);
        setDisclaimer({
          version: 'current',
          effective_date: 'now',
          text: 'By creating an account, you agree to our Terms of Service and Privacy Notice.',
          checkbox_label: 'I agree to the terms above',
        });
      });
  }, []);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!form.first_name.trim() || !form.last_name.trim()) {
      setLocalError('Please enter your full name.');
      return;
    }
    if (!form.email.trim()) {
      setLocalError('Please enter your email address.');
      return;
    }
    if (form.password.length < 8) {
      setLocalError('Password must be at least 8 characters.');
      return;
    }
    if (form.password !== form.confirm_password) {
      setLocalError('Passwords do not match.');
      return;
    }
    if (!acceptedDisclaimer) {
      setLocalError('You must accept the legal disclaimer to create an account.');
      return;
    }

    setSubmitting(true);
    try {
      const { confirm_password, ...payload } = form;
      await onRegister({
        ...payload,
        phone: payload.phone.trim() || undefined,
        accepted_disclaimer: acceptedDisclaimer,
        disclaimer_version: disclaimer?.version,
        ...(signupIntent ? { signup_intent: signupIntent } : {}),
      });
    } catch (err) {
      setLocalError(err.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const displayError = localError || error;
  const checkboxLabelPrefix = (disclaimer?.checkbox_label || FALLBACK_CHECKBOX_LABEL)
    .split('Terms of Service')[0];

  return (
    <div className="auth-page">
      <div className="auth-card auth-card--wide">
        <AuthHomeLink onHome={onHome} />
        <div className="auth-brand auth-brand--logo">
          <img src={logo} alt="cutthe6.com" className="auth-brand-logo" />
          <p className="auth-brand-tagline">Sell your home. Keep more of your money.</p>
        </div>
        <h1 className="auth-title">Create account</h1>
        <p className="auth-subtitle">
          {listingSignup
            ? 'Create your account to finish your listing'
            : 'Use basic details to continue — accounts start with buyer access by default'}
        </p>

        {displayError && (
          <div className="auth-error" role="alert">
            {displayError}
          </div>
        )}

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="auth-row">
            <div className="auth-field">
              <label htmlFor="reg-first" className="auth-label">First name</label>
              <input
                id="reg-first"
                type="text"
                className="auth-input"
                placeholder="Alex"
                value={form.first_name}
                onChange={set('first_name')}
                autoComplete="given-name"
                required
                disabled={submitting}
              />
            </div>
            <div className="auth-field">
              <label htmlFor="reg-last" className="auth-label">Last name</label>
              <input
                id="reg-last"
                type="text"
                className="auth-input"
                placeholder="Smith"
                value={form.last_name}
                onChange={set('last_name')}
                autoComplete="family-name"
                required
                disabled={submitting}
              />
            </div>
          </div>

          <div className="auth-field">
            <label htmlFor="reg-email" className="auth-label">Email</label>
            <input
              id="reg-email"
              type="email"
              className="auth-input"
              placeholder="you@example.com"
              value={form.email}
              onChange={set('email')}
              autoComplete="email"
              required
              disabled={submitting}
            />
          </div>

          <div className="auth-field">
            <label htmlFor="reg-phone" className="auth-label">
              Phone <span className="auth-optional">(optional)</span>
            </label>
            <input
              id="reg-phone"
              type="tel"
              className="auth-input"
              placeholder="555-000-1234"
              value={form.phone}
              onChange={set('phone')}
              autoComplete="tel"
              disabled={submitting}
            />
          </div>

          <p className="auth-role-note">
            <span aria-hidden="true">🏠</span>{' '}
            {listingSignup
              ? 'Your account can list your home and make offers on others'
              : 'All new accounts start as Buyer access'}
          </p>

          <div className="auth-field">
            <label htmlFor="reg-password" className="auth-label">Password</label>
            <div className="auth-input-wrap">
              <input
                id="reg-password"
                type={showPassword ? 'text' : 'password'}
                className="auth-input auth-input--with-toggle"
                placeholder="••••••••"
                value={form.password}
                onChange={set('password')}
                autoComplete="new-password"
                required
                disabled={submitting}
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowPassword((s) => !s)}
                disabled={submitting}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <div className="auth-field">
            <label htmlFor="reg-confirm" className="auth-label">Confirm password</label>
            <div className="auth-input-wrap">
              <input
                id="reg-confirm"
                type={showPassword ? 'text' : 'password'}
                className="auth-input auth-input--with-toggle"
                placeholder="••••••••"
                value={form.confirm_password}
                onChange={set('confirm_password')}
                autoComplete="new-password"
                required
                disabled={submitting}
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowPassword((s) => !s)}
                disabled={submitting}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <div className="auth-disclaimer">
            {disclaimer ? (
              <>
                <div className="auth-disclaimer-header">
                  <span className="auth-disclaimer-ref">
                    Ref: NRT-DISC-{disclaimer.version}
                  </span>
                  <span className="auth-disclaimer-date">
                    Effective {disclaimer.effective_date}
                  </span>
                </div>
                <p className="auth-disclaimer-text">{disclaimer.text}</p>
                <label className="auth-disclaimer-label">
                  <input
                    id="reg-disclaimer"
                    type="checkbox"
                    checked={acceptedDisclaimer}
                    onChange={(e) => setAcceptedDisclaimer(e.target.checked)}
                    disabled={submitting}
                  />
                  {checkboxLabelPrefix}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Notice</a>.
                </label>
              </>
            ) : (
              <p className="auth-disclaimer-text">Loading disclaimer…</p>
            )}
          </div>

          <button
            type="submit"
            className="auth-btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <p className="auth-switch">
          Have an account?{' '}
          <button
            type="button"
            className="auth-link-btn"
            onClick={onSwitchToLogin}
            disabled={submitting}
          >
            Sign in
          </button>
        </p>

        <LegalFooterLinks className="auth-legal-links" />
      </div>
    </div>
  );
};

export default RegisterPage;
